import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { documentTitle, titleForPath } from '@/lib/page-titles';
import { AuthProvider } from '@/hooks/useAuth';
import { I18nProvider } from '@/hooks/useI18n';
import { ProtectedRoute } from '@/components/protected-route';
import { useAuth } from '@/hooks/useAuth';
import { PERSONEL_ROLU } from '@/lib/auth';

const DashboardPage = lazy(() => import('./pages/dashboard'));
const LoginPage = lazy(() => import('./pages/giris'));
const SignupPage = lazy(() => import('./pages/kayit'));
const ForgotPasswordPage = lazy(() => import('./pages/sifremi-unuttum'));
const SifreBelirlePage = lazy(() => import('./pages/sifre-belirle'));
const SifreDegistirPage = lazy(() => import('./pages/sifre-degistir'));
const SettingsLayout = lazy(() => import('./pages/ayarlar/layout'));
const ProfilePage = lazy(() => import('./pages/ayarlar/profilim'));
const ContractsPage = lazy(() => import('./pages/ayarlar/profilim/sozlesmeler'));
const TasksPage = lazy(() => import('./pages/ayarlar/profilim/gorevler'));
const DocumentsPage = lazy(() => import('./pages/ayarlar/profilim/evraklar'));
const TrainingsPage = lazy(() => import('./pages/ayarlar/profilim/egitimler'));
const AssetsPage = lazy(() => import('./pages/ayarlar/profilim/zimmetler'));
const LeavesPage = lazy(() => import('./pages/ayarlar/profilim/izinler'));
const ExpensesPage = lazy(() => import('./pages/ayarlar/profilim/masraflar'));
const RecordsPage = lazy(() => import('./pages/ayarlar/profilim/kayitlarim'));
const RequestsPage = lazy(() => import('./pages/ayarlar/profilim/talepler'));
const LogsPage = lazy(() => import('./pages/ayarlar/profilim/loglar'));
const PerformancePage = lazy(() => import('./pages/ayarlar/profilim/performans'));
const PublicFormPage = lazy(() => import('./pages/f/[formId]'));
const PaylasimSayfasi = lazy(() => import('./pages/p/[token]'));
const KullaniciYonetimiPage = lazy(() => import('./pages/kullanici-yonetimi'));
const DilYonetimiPage = lazy(() => import('./pages/dil-yonetimi'));
const SistemLoglariPage = lazy(() => import('./pages/sistem-loglari'));
const SirketYonetimiPage = lazy(() => import('./pages/sirket-yonetimi'));
const DenetimSetleriPage = lazy(() => import('./pages/denetim-setleri'));
const DenetimSetiDetayPage = lazy(() => import('./pages/denetim-setleri/[id]'));
const SirketSablonlariPage = lazy(() => import('./pages/sirket-sablonlari'));
const SirketSablonuTasarimPage = lazy(() => import('./pages/sirket-sablonlari/tasarim'));
// Tasarım bölümünün henüz içeriği olmayan sayfaları (menüde yeri açıldı).
const TasarimAlanlarPage = lazy(() => import('./pages/tasarim/alanlar'));
const DokumanKategorileriPage = lazy(() => import('./pages/dokuman-kategorileri'));
const DokumantasyonPage = lazy(() => import('./pages/dokumantasyon/index'));
const DocumentDetailPage = lazy(() => import('./pages/dokumantasyon/[id]'));
const CopKutusuPage = lazy(() => import('./pages/dokumantasyon/cop-kutusu'));
const KapsamlarPage = lazy(() => import('./pages/kapsamlar'));
const MetaAlanTanimlariPage = lazy(() => import('./pages/meta-alan-tanimlari'));
const IcerikTipleriPage = lazy(() => import('./pages/icerik-tipleri'));
const IcerikTipiDetayPage = lazy(() => import('./pages/icerik-tipleri/[key]'));
const IcerikTipiYerlesimPage = lazy(() => import('./pages/icerik-tipleri/yerlesim'));
const MusterilerPage = lazy(() => import('./pages/musteriler'));
const MusteriDetayPage = lazy(() => import('./pages/musteriler/[id]'));
const KontrolListeleriPage = lazy(() => import('./pages/kontrol-listeleri'));
const KontrolListesiDetayPage = lazy(() => import('./pages/kontrol-listeleri/[id]'));
const KayitlarPage = lazy(() => import('./pages/kayitlar'));
const BildirimlerPage = lazy(() => import('./pages/bildirimler'));
const GorevlerimPage = lazy(() => import('./pages/gorevlerim'));
const PersonelDetayPage = lazy(() => import('./pages/personeller/detail-page'));
const SirketDetayPage = lazy(() => import('./pages/sirketler/detail'));
const KayitDoldurPage = lazy(() => import('./pages/kayitlar/doldur'));
const AsansorProjeleriPage = lazy(() => import('./pages/asansor-projeleri'));
const AsansorProjeleriYeniPage = lazy(() => import('./pages/asansor-projeleri/yeni'));
const AsansorProjeDetayPage = lazy(() => import('./pages/asansor-projeleri/[id]'));
const AsansorProjeDuzenlePage = lazy(() => import('./pages/asansor-projeleri/[id]/duzenle'));
const AsansorOturumPage = lazy(() => import('./pages/asansor-projeleri/[id]/oturum/[sessionId]'));
const AsansorOturumYazdirPage = lazy(() => import('./pages/asansor-projeleri/[id]/oturum/yazdir'));
const AsansorFormYazdirPage = lazy(() => import('./pages/asansor-projeleri/[id]/form-yazdir'));
const EquipmentPage = lazy(() => import('./pages/cihaz-yonetimi'));
const PersonellerPage = lazy(() => import('./pages/personeller'));
const PortalDosyalarPage = lazy(() => import('./pages/portal'));
const PortalDosyaDetayPage = lazy(() => import('./pages/portal/[id]'));
const NotFoundPage = lazy(() => import('./pages/404'));

/**
 * Sekme başlığını rotaya göre ayarlar.
 *
 * Tek yerde: yirmi sayfaya tek tek başlık yazmak, yeni sayfa eklendiğinde
 * unutulacak bir iş olurdu. Belirli bir varlık gösteren ekranlar kendi adını
 * `useDocumentTitle` ile bunun ÜSTÜNE yazıyor.
 */
function DocumentTitle() {
  const { pathname } = useLocation()
  useEffect(() => {
    document.title = documentTitle(titleForPath(pathname))
  }, [pathname])
  return null
}

function Loading() {
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <DocumentTitle />
        <Suspense fallback={<Loading />}>
          <Routes>
          {/* Public routes */}
          <Route path="/giris" element={<LoginPage />} />
          <Route path="/kayit" element={<SignupPage />} />
          <Route path="/sifremi-unuttum" element={<ForgotPasswordPage />} />
          <Route path="/sifre-belirle" element={<SifreBelirlePage />} />
          <Route path="/f/:formId" element={<PublicFormPage />} />
          {/* Paylaşılan belge (e-posta / WhatsApp bağlantısı) — oturumsuz. */}
          <Route path="/p/:token" element={<PaylasimSayfasi />} />

          {/* Protected routes */}
          <Route path="/" element={<ProtectedRoute><AnaSayfa /></ProtectedRoute>} />
          <Route path="/sifre-degistir" element={<ProtectedRoute><SifreDegistirPage /></ProtectedRoute>} />
          <Route path="/ayarlar" element={<ProtectedRoute><SettingsLayout /></ProtectedRoute>}>
            <Route index element={<ProfilePage />} />
            <Route path="profilim" element={<ProfilePage />} />
            <Route path="profilim/sozlesmeler" element={<ContractsPage />} />
            <Route path="profilim/gorevler" element={<TasksPage />} />
            <Route path="profilim/evraklar" element={<DocumentsPage />} />
            <Route path="profilim/egitimler" element={<TrainingsPage />} />
            <Route path="profilim/zimmetler" element={<AssetsPage />} />
            <Route path="profilim/izinler" element={<LeavesPage />} />
            <Route path="profilim/masraflar" element={<ExpensesPage />} />
            <Route path="profilim/kayitlarim" element={<RecordsPage />} />
            <Route path="profilim/talepler" element={<RequestsPage />} />
            <Route path="profilim/loglar" element={<LogsPage />} />
            <Route path="profilim/performans" element={<PerformancePage />} />
          </Route>
          <Route path="/kullanici-yonetimi" element={<ProtectedRoute allowedRoles={["super_admin", "admin"]}><KullaniciYonetimiPage /></ProtectedRoute>} />
          <Route path="/dil-yonetimi" element={<ProtectedRoute allowedRoles={["super_admin"]}><DilYonetimiPage /></ProtectedRoute>} />
          <Route path="/sistem-loglari" element={<ProtectedRoute allowedRoles={["super_admin"]}><SistemLoglariPage /></ProtectedRoute>} />
          <Route path="/sirket-yonetimi" element={<ProtectedRoute allowedRoles={["admin"]}><SirketYonetimiPage /></ProtectedRoute>} />
          <Route path="/sirketler/:id" element={<ProtectedRoute allowedRoles={["admin"]}><SirketDetayPage /></ProtectedRoute>} />
          <Route path="/sirketim" element={<ProtectedRoute allowedRoles={["company"]}><SirketDetayPage /></ProtectedRoute>} />
          <Route path="/denetim-setleri" element={<ProtectedRoute allowedRoles={["admin", "company"]}><DenetimSetleriPage /></ProtectedRoute>} />
          <Route path="/denetim-setleri/:id" element={<ProtectedRoute allowedRoles={["admin", "company"]}><DenetimSetiDetayPage /></ProtectedRoute>} />
          <Route path="/sirket-sablonlari" element={<ProtectedRoute allowedRoles={["admin", "company"]}><SirketSablonlariPage /></ProtectedRoute>} />
          {/* Tasarımcı yeni sekmede, sidebar'sız açılır (detay sayfalarıyla aynı desen). */}
          <Route path="/sirket-sablonlari/:id/tasarim" element={<ProtectedRoute allowedRoles={["admin", "company"]}><SirketSablonuTasarimPage /></ProtectedRoute>} />
          {/* DOKÜMANLAR — kontrol listesi ekranlarını türe göre paylaşıyor
              (bkz. `useChecklistKind`); gövde bölüm ağacı. */}
          <Route path="/tasarim/dokumanlar" element={<ProtectedRoute allowedRoles={["admin", "company"]}><KontrolListeleriPage /></ProtectedRoute>} />
          <Route path="/tasarim/dokumanlar/:id" element={<ProtectedRoute allowedRoles={["admin", "company"]}><KontrolListesiDetayPage /></ProtectedRoute>} />
          <Route path="/tasarim/alanlar" element={<ProtectedRoute allowedRoles={["admin", "company"]}><TasarimAlanlarPage /></ProtectedRoute>} />
          <Route path="/dokuman-kategorileri" element={<ProtectedRoute allowedRoles={["admin"]}><DokumanKategorileriPage /></ProtectedRoute>} />
          <Route path="/dokumantasyon" element={<ProtectedRoute allowedRoles={["admin", "company"]}><DokumantasyonPage /></ProtectedRoute>} />
          <Route path="/dokumantasyon/cop-kutusu" element={<ProtectedRoute allowedRoles={["admin", "company"]}><CopKutusuPage /></ProtectedRoute>} />
          <Route path="/cop-kutusu" element={<ProtectedRoute allowedRoles={["admin", "company"]}><CopKutusuPage /></ProtectedRoute>} />
          <Route path="/dokumantasyon/:id" element={<ProtectedRoute allowedRoles={["admin", "company"]}><DocumentDetailPage /></ProtectedRoute>} />
          <Route path="/kapsamlar" element={<ProtectedRoute allowedRoles={["admin"]}><KapsamlarPage /></ProtectedRoute>} />
          <Route path="/meta-alan-tanimlari" element={<ProtectedRoute allowedRoles={["admin"]}><MetaAlanTanimlariPage /></ProtectedRoute>} />
          <Route path="/formlar" element={<ProtectedRoute allowedRoles={["admin"]}><IcerikTipleriPage /></ProtectedRoute>} />
          <Route path="/formlar/:key" element={<ProtectedRoute allowedRoles={["admin"]}><IcerikTipiDetayPage /></ProtectedRoute>} />
          <Route path="/formlar/:key/yerlesim" element={<ProtectedRoute allowedRoles={["admin"]}><IcerikTipiYerlesimPage /></ProtectedRoute>} />
          {/* Eski adresler: kayıtlı bildirim bağlantıları ve yer imleri hâlâ buraya gidiyor. */}
          <Route path="/icerik-tipleri/*" element={<EskiAdresYonlendirme from="/icerik-tipleri" to="/formlar" />} />
          <Route path="/projeler/*" element={<EskiAdresYonlendirme from="/projeler" to="/faaliyetler" />} />

          {/*
            KAPAKLAR — içerik tipinin ikinci türü, AYNI sayfaları kullanıyor.
            Tür rotadan çözülüyor (`useContentTypeKind`): ikinci bir liste,
            detay ve tasarımcı yazmak, tasarlarken gördüğünle bastığın belgenin
            zamanla ayrışması demekti.
          */}
          <Route path="/kapaklar" element={<ProtectedRoute allowedRoles={["admin"]}><IcerikTipleriPage /></ProtectedRoute>} />
          <Route path="/kapaklar/:key" element={<ProtectedRoute allowedRoles={["admin"]}><IcerikTipiDetayPage /></ProtectedRoute>} />
          <Route path="/kapaklar/:key/yerlesim" element={<ProtectedRoute allowedRoles={["admin"]}><IcerikTipiYerlesimPage /></ProtectedRoute>} />
          <Route path="/portal" element={<ProtectedRoute allowedRoles={["customer"]}><PortalDosyalarPage /></ProtectedRoute>} />
          <Route path="/portal/dosyalar/:id" element={<ProtectedRoute allowedRoles={["customer"]}><PortalDosyaDetayPage /></ProtectedRoute>} />
          <Route path="/musteriler" element={<ProtectedRoute allowedRoles={["admin", "company"]}><MusterilerPage /></ProtectedRoute>} />
          <Route path="/musteriler/:id" element={<ProtectedRoute allowedRoles={["admin", "company"]}><MusteriDetayPage /></ProtectedRoute>} />
          {/* Bildirimler herkese açık: bildirim kişiseldir, rol kısıtı yok. */}
          <Route path="/bildirimler" element={<ProtectedRoute><BildirimlerPage /></ProtectedRoute>} />
          <Route path="/gorevlerim" element={<ProtectedRoute><GorevlerimPage /></ProtectedRoute>} />
          <Route path="/kayitlar" element={<ProtectedRoute allowedRoles={["admin", "company"]}><KayitlarPage /></ProtectedRoute>} />
          {/* Doldurma: yeni kayıt (:key) ve var olan kayıt (:id) tek bileşen. */}
          <Route path="/kayitlar/:key/yeni" element={<ProtectedRoute allowedRoles={["admin", "company"]}><KayitDoldurPage /></ProtectedRoute>} />
          <Route path="/kayitlar/kayit/:id" element={<ProtectedRoute allowedRoles={["admin", "company"]}><KayitDoldurPage /></ProtectedRoute>} />
          <Route path="/kontrol-listeleri" element={<ProtectedRoute allowedRoles={["admin", "company"]}><KontrolListeleriPage /></ProtectedRoute>} />
          <Route path="/kontrol-listeleri/:id" element={<ProtectedRoute allowedRoles={["admin", "company"]}><KontrolListesiDetayPage /></ProtectedRoute>} />
          <Route path="/faaliyetler" element={<ProtectedRoute allowedRoles={["admin", "company", "personnel"]}><AsansorProjeleriPage /></ProtectedRoute>} />
          <Route path="/faaliyetler/yeni" element={<ProtectedRoute allowedRoles={["admin", "company"]}><AsansorProjeleriYeniPage /></ProtectedRoute>} />
          <Route path="/faaliyetler/:id" element={<ProtectedRoute allowedRoles={["admin", "company", "personnel"]}><AsansorProjeDetayPage /></ProtectedRoute>} />
          <Route path="/faaliyetler/:id/duzenle" element={<ProtectedRoute allowedRoles={["admin", "company"]}><AsansorProjeDuzenlePage /></ProtectedRoute>} />
          <Route path="/faaliyetler/:id/oturum/:sessionId" element={<ProtectedRoute allowedRoles={["admin", "company", "personnel"]}><AsansorOturumPage /></ProtectedRoute>} />
          {/* Dosyadan yazdırma: kapak + antet (dosya künyesiyle) + dolu liste. */}
          <Route path="/faaliyetler/:id/oturum/:sessionId/yazdir" element={<ProtectedRoute allowedRoles={["admin", "company", "personnel"]}><AsansorOturumYazdirPage /></ProtectedRoute>} />
          {/* Dosyadan form yazdırma: antet + dosya değerleriyle dolu form. */}
          <Route path="/faaliyetler/:id/form/:formKey/yazdir" element={<ProtectedRoute allowedRoles={["admin", "company", "personnel"]}><AsansorFormYazdirPage /></ProtectedRoute>} />
          <Route path="/personeller" element={<ProtectedRoute allowedRoles={["admin", "company"]}><PersonellerPage /></ProtectedRoute>} />
          <Route path="/personeller/:id" element={<ProtectedRoute allowedRoles={["admin", "company"]}><PersonelDetayPage /></ProtectedRoute>} />
          <Route path="/cihaz-yonetimi" element={<ProtectedRoute allowedRoles={["admin", "company"]}><EquipmentPage /></ProtectedRoute>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AuthProvider>
    </I18nProvider>
  );
}

/** Ana sayfa: personelin işi görevleri — panel firma yönetimine ait. */
function AnaSayfa() {
  const { user } = useAuth();
  if (user?.role === PERSONEL_ROLU) return <Navigate to="/gorevlerim" replace />;
  return <DashboardPage />;
}

/** Eski rota kökünü yenisine çevirir (`/projeler/42?x` → `/faaliyetler/42?x`); yol, sorgu ve hash korunur. */
function EskiAdresYonlendirme({ from, to }: { from: string; to: string }) {
  const { pathname, search, hash } = useLocation();
  return <Navigate replace to={to + pathname.slice(from.length) + search + hash} />;
}
